import { ChangeLanguage } from "components/core/ChangeLanguage";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NavSmallScreen } from "./NavSmallScreen";
import { useAuth } from "context/auth-context";
import { HashLink } from "react-router-hash-link";
import { useLocation, useNavigate } from "react-router-dom";
import { UserOutlined, DownOutlined, BellOutlined } from "@ant-design/icons";
import { Avatar, Dropdown } from "antd";
import { getNotificationDetails } from "components/Notifications/Notifications.api";
import { useQuery } from "@tanstack/react-query";
import { axiosClient } from "api/api";
import { ThemeToggle } from "components/core/ThemeToggle/ThemeToggle";
import { BrandLogo } from "components/ui";

// type ToggleMobileMenu = () => void;

export const Header = () => {
  const { t } = useTranslation();
  const [isNavOpen, setIsNavOpen] = useState<boolean>(false);
  const { auth } = useAuth();
  const [currentPage] = useState<number>(1);
  const [langCode, setLangCode] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const normalizedPath = location.pathname.replace(/\/+$/, "");
  const hideFooterRoutes = ["/success", "/terminate", "/quota-full", "/security-terminate"];
  const hideContent = hideFooterRoutes.includes(normalizedPath);

  const scrollToTop = () => {
    window.scrollTo({
      top: 10,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    setIsNavOpen(false);
  }, [location]);

  const getNotificationsData = useQuery(
    ["getNotifications", currentPage, langCode],
    () => {
      if (langCode) {
        if (auth) {
          return getNotificationDetails(currentPage, langCode, auth?.panelistId);
        } else {
          return [];
        }
      } else {
        return [];
      }
    },
    {
      refetchOnWindowFocus: false,
    }
  );
  const handleGetStarted = () => {
    location.pathname === "/join-now" ? window.scrollTo(0, 0) : navigate("/join-now");
  };

  const getData = async () => {
    const ipUrl = `/api/user/get-ip-details`;
    const res = await axiosClient.get(ipUrl, { timeout: 5000, });
    if (res.data.data.country.currency.code == "USD") {
      setLangCode("En-US");
    } else if (res.data.data.country.currency.code == "CAD") {
      setLangCode("English-CA");
    } else if (res.data.data.country.currency.code == "GBP") {
      setLangCode("En-US");
    } else if (res.data.data.country.currency.code == "INR") {
      setLangCode("English-IN");
    } else {
      setLangCode(res.data.data.country.name + "-" + res.data.data.country.currency.code);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // const mobileMenuToggle: ToggleMobileMenu = () => setIsNavOpen((prev) => !prev);
  const mobileMenuToggle = () => {
    setIsNavOpen(!isNavOpen);
  };
  const items = [
    {
      key: "profile_1",
      className: "header-ant-dropdown-menu",
      label: <Link to={"/my-surveys"}>{t("home.headers.survey")}</Link>,
    },
    {
      key: "profile_2",
      className: "header-ant-dropdown-menu",
      label: <Link to={"/my-profile"}>{t("home.headers.profile")}</Link>,
    },
    {
      key: "profile_3",
      className: "header-ant-dropdown-menu",
      label: <Link to={"/my-referral"}>{t("home.headers.referral")}</Link>,
    },
    {
      key: "profile_4",
      className: `header-ant-dropdown-menu ${!auth?.isVirtualWalletOpen ? "hidden_menu" : ""}`,
      label: <Link to={"/virtual-wallet"}>{t("home.headers.virtual")}</Link>,
    },
    {
      key: "profile_5",
      className: "header-ant-dropdown-menu",
      label: <Link to={"/history"}>{t("home.headers.history")}</Link>,
    },
    {
      key: "profile_6",
      className: "header-ant-dropdown-menu",
      label: <Link to={"/unsubscribe"}>{t("home.headers.unsubscribe")}</Link>,
    },
    {
      key: "profile_7",
      className: "header-ant-dropdown-menu",
      label: (
        <Link
          to={"/logout"}
        // onClick={(event) => {
        //   event.preventDefault();
        //   // navigate(location.state || "/logout", { state: null });
        // }}
        >
          {t("home.headers.logout")}
        </Link>
      ),
    },
  ];
  // const modalDisplayed = localStorage.getItem("modalDisplayed");

  return (
    <div className="zs-public-header">
      <div className="flex items-center justify-between w-full h-full gap-6 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <Link to={"/"} onClick={scrollToTop} className="flex items-center shrink-0" aria-label="Zamplia Surveys home">
          <BrandLogo className="w-auto h-8 lg:h-9" />
        </Link>

        <nav className="items-center justify-center flex-1 hidden gap-8 lg:flex" aria-label="Primary">
          {!hideContent && (
            <>
              <HashLink data-testid="home_link" smooth to="/" className="zs-public-link capitalize" onClick={scrollToTop}>
                {t("homes.headers.Home")}
              </HashLink>
              <HashLink data-testid="faq_link" smooth to="/#feature" className="zs-public-link">
                Features
              </HashLink>
              <HashLink data-testid="contact_link" smooth to="/#contact" className="zs-public-link capitalize">
                {t("home.footer.Contact")}
              </HashLink>
            </>
          )}
          {auth && (
            <>
              <HashLink data-testid="query_link" smooth to="/raise-query" className="zs-public-link capitalize">
                {t("home.headers.Query")}
              </HashLink>
              <HashLink smooth to="/alert" className="relative zs-icon-btn" aria-label="Notifications">
                <BellOutlined />
                <span className="zs-count-badge">
                  {getNotificationsData?.data?.totalUsers == undefined ? "0" : getNotificationsData.data?.totalUsers}
                </span>
              </HashLink>
              <Dropdown menu={{ items }}>
                <a onClick={(e) => e.preventDefault()} className="flex items-center gap-2 text-sm font-medium text-ink-2">
                  <Avatar size={30} icon={<UserOutlined />} className="!bg-primary-soft !text-primary-text" />
                  Hi! {auth.name ? auth.name.split(" ")[0] : ""} <DownOutlined className="text-xs" />
                </a>
              </Dropdown>
            </>
          )}
        </nav>

        <div className="items-center hidden gap-2 lg:flex shrink-0">
          <ThemeToggle />
          <ChangeLanguage />
          {!auth && !hideContent && (
            <button onClick={handleGetStarted} className="ml-2 zs-btn zs-btn-primary">
              Get Started Now
            </button>
          )}
        </div>

        <section className="flex items-center gap-1 MOBILE-MENU lg:hidden">
          <ThemeToggle compact />
          <button
            data-testid="hamburgerBtn"
            className="flex flex-col items-center justify-center gap-1.5 zs-icon-btn HAMBURGER-ICON"
            onClick={mobileMenuToggle}
            aria-label="Open menu"
          >
            <span className=""></span>
            <span className=""></span>
            <span className=""></span>
          </button>
          {isNavOpen ? <NavSmallScreen toggle={mobileMenuToggle} /> : null}
        </section>
      </div>
    </div>
  );
};
